import { useState } from 'react';

import { baseClient } from 'soapbox/api/index.ts';
import {
  clearStagingAdminAuthorization,
  setStagingAdminAuthorization,
} from 'soapbox/utils/staging-admin-gate.ts';

import type { AxiosError } from 'axios';

interface StagingAdminGateProps {
  onAuthorized: () => void;
}

interface OAuthAppResponse {
  client_id: string;
  client_secret: string;
}

interface OAuthTokenResponse {
  access_token: string;
}

const GATE_APP_STORAGE_KEY = 'soapbox:staging_admin_gate_app';
const GATE_TITLE = 'Staging Access';
const GATE_SUBTITLE = 'Sign in with a site admin account to access staging.';
const GATE_USERNAME_LABEL = 'Username or email';
const GATE_PASSWORD_LABEL = 'Password';
const GATE_CHECKING_LABEL = 'Checking...';
const GATE_CONTINUE_LABEL = 'Continue';

const isOAuthApp = (value: unknown): value is OAuthAppResponse => {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'client_id' in value &&
      'client_secret' in value,
  );
};

const readCachedGateApp = (): OAuthAppResponse | null => {
  try {
    const stored = localStorage.getItem(GATE_APP_STORAGE_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return isOAuthApp(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const cacheGateApp = (app: OAuthAppResponse) => {
  localStorage.setItem(GATE_APP_STORAGE_KEY, JSON.stringify(app));
};

const isAdminAccount = (account: any): boolean => {
  return Boolean(
    account?.admin ||
      account?.role === 'admin' ||
      account?.staff ||
      account?.pleroma?.is_admin,
  );
};

const isAxiosError = (
  error: unknown,
): error is AxiosError<{ error?: string; error_description?: string }> => {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'isAxiosError' in error &&
      (error as { isAxiosError?: boolean }).isAxiosError,
  );
};

const getErrorMessage = (error: unknown) => {
  if (isAxiosError(error) && error.response?.data?.error === 'mfa_required') {
    return 'This account requires MFA and cannot be used for staging gate login.';
  }

  if (isAxiosError(error) && error.response?.status === 401) {
    return 'Invalid username or password.';
  }

  if (isAxiosError(error) && error.response?.status === 403) {
    return 'This account does not have admin access.';
  }

  if (isAxiosError(error) && error.response?.data?.error_description) {
    return error.response.data.error_description;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Unable to verify admin credentials right now.';
};

const StagingAdminGate = ({ onAuthorized }: StagingAdminGateProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const authenticate = async () => {
    const appParams = {
      client_name: 'TrueMaple Staging Gate',
      redirect_uris: 'urn:ietf:wg:oauth:2.0:oob',
      scopes: 'read',
      website: 'https://staging.truemaple.net/',
    };
    const app =
      readCachedGateApp() ||
      (
        await baseClient().post<OAuthAppResponse>(
          '/api/v1/apps',
          appParams,
        )
      ).data;

    cacheGateApp(app);

    const { data: token } = await baseClient().post<OAuthTokenResponse>(
      '/oauth/token',
      {
        client_id: app.client_id,
        client_secret: app.client_secret,
        redirect_uri: 'urn:ietf:wg:oauth:2.0:oob',
        grant_type: 'password',
        username: username.trim(),
        password,
        scope: 'read',
      },
    );

    try {
      const { data: account } = await baseClient(token.access_token).get(
        '/api/v1/accounts/verify_credentials',
      );

      if (!isAdminAccount(account)) {
        throw new Error('This account does not have admin access.');
      }
    } finally {
      // Best effort revoke to avoid keeping an access token around.
      await baseClient()
        .post('/oauth/revoke', {
          client_id: app.client_id,
          client_secret: app.client_secret,
          token: token.access_token,
        })
        .catch(() => undefined);
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authenticate();
      setStagingAdminAuthorization();
      onAuthorized();
    } catch (caughtError: unknown) {
      clearStagingAdminAuthorization();
      setError(getErrorMessage(caughtError));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className='flex min-h-screen items-center justify-center bg-slate-950 p-4'>
      <section className='w-full max-w-sm rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl'>
        <h1 className='text-xl font-semibold text-slate-100'>
          {GATE_TITLE}
        </h1>
        <p className='mt-2 text-sm text-slate-400'>
          {GATE_SUBTITLE}
        </p>

        <form className='mt-6 space-y-4' onSubmit={handleSubmit}>
          <label className='block'>
            <span className='mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300'>
              {GATE_USERNAME_LABEL}
            </span>
            <input
              type='text'
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              className='w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-sky-400'
              autoComplete='username'
              required
            />
          </label>

          <label className='block'>
            <span className='mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300'>
              {GATE_PASSWORD_LABEL}
            </span>
            <input
              type='password'
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className='w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-sky-400'
              autoComplete='current-password'
              required
            />
          </label>

          {error && <p className='text-sm text-rose-400'>{error}</p>}

          <button
            type='submit'
            disabled={isLoading}
            className='w-full rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {isLoading ? GATE_CHECKING_LABEL : GATE_CONTINUE_LABEL}
          </button>
        </form>
      </section>
    </main>
  );
};

export default StagingAdminGate;
